import type { GameAudio } from '../audio/GameAudio';
import type { Bout } from '../fight/Bout';
import { BOUT, STAGE } from '../fight/data';
import { CALM_PATIENCE_PER_CUSTOMER, Interlude, type InterludeResult } from '../fight/Interlude';
import type { FightEvent, PlayerInput } from '../fight/types';
import { SIDE_H, sideSprite } from '../render/fighterSide';
import { FightStage } from '../render/fightStage';
import { dottieSprite } from '../render/characters';
import { paintSpriteInto } from '../render/sprite';
import { h, kids, onPress, setText, toggleClass } from './dom';

export interface FightStartOptions {
  /** Night number that just ended (fights get rowdier each night). */
  level: number;
  seed: number;
  /** Practice from the menu rather than a real shift change. */
  practice: boolean;
  label: string;
  reducedMotion: boolean;
  showKeys: boolean;
  /** Returns achievements newly unlocked by this interlude, for the summary card. */
  unlock: (result: InterludeResult) => { emoji: string; title: string; description: string }[];
}

export interface FightCallbacks {
  /** `result` is null if the fight was skipped. */
  onComplete(result: InterludeResult | null, next: 'clockIn' | 'menu'): void;
}

type Held = 'left' | 'right' | 'block';
type Tap = 'jab' | 'toss' | 'super';

const TIPS = [
  'They flash RED right before they swing — hold BLOCK.',
  'Tap JAB up close. Three in a row = spatula smack.',
  'Block right BEFORE the hit to PARRY. Yes, you can catch a chair.',
  'Keep your distance and throw a HASH BROWN.',
  'Big windups flash ORANGE — mashing JAB won’t stop those. Block!',
  'Meter full? Go ALL THE WAY.',
] as const;

const KEYMAP: Record<string, Held | Tap | 'pause'> = {
  arrowleft: 'left',
  a: 'left',
  arrowright: 'right',
  d: 'right',
  arrowdown: 'block',
  s: 'block',
  l: 'block',
  c: 'block',
  j: 'jab',
  z: 'jab',
  k: 'toss',
  x: 'toss',
  ' ': 'super',
  i: 'super',
  arrowup: 'super',
  w: 'super',
  escape: 'pause',
  p: 'pause',
};

function portrait(side: Parameters<typeof sideSprite>[0], className: string): HTMLCanvasElement {
  const c = h('canvas', { class: className });
  paintSpriteInto(c, sideSprite(side, 'idle', 0), 3);
  return c;
}

/**
 * The shift-change brawl: a lineup card, three bouts, and a summary. Owns its
 * own fixed-step loop, input and HUD; App just forwards frames and keys.
 */
export class FightScreen {
  readonly el: HTMLElement;
  private readonly stage: FightStage;
  private readonly stageEl: HTMLElement;
  private readonly label: HTMLElement;
  private readonly card: HTMLElement;
  private readonly pauseCard: HTMLElement;
  private readonly hud: HTMLElement;
  private readonly coolFill: HTMLElement;
  private readonly rageFill: HTMLElement;
  private readonly superFill: HTMLElement;
  private readonly superWrap: HTMLElement;
  private readonly oppName: HTMLElement;
  private readonly timer: HTMLElement;
  private readonly pips: HTMLElement;
  private readonly combo: HTMLElement;
  private readonly banner: HTMLElement;
  private readonly bannerText: HTMLElement;
  private readonly bannerSub: HTMLElement;
  private readonly callouts: HTMLElement;
  private readonly bubble: HTMLElement;
  private readonly dottie: HTMLElement;
  private readonly dottieLine: HTMLElement;
  private readonly superButton: HTMLButtonElement;
  private readonly keysHint: HTMLElement;
  private readonly tip: HTMLElement;
  private tipIndex = 0;
  private tipAt = 0;

  private interlude: Interlude | null = null;
  private bout: Bout | null = null;
  private unsub: (() => void) | null = null;
  private options: FightStartOptions | null = null;
  private mode: 'idle' | 'intro' | 'bout' | 'summary' = 'idle';
  private paused = false;
  private acc = 0;
  private now = 0;
  private bannerUntil = 0;
  private bubbleUntil = 0;
  private dottieUntil = 0;
  private readonly held: Record<Held, boolean> = { left: false, right: false, block: false };
  private pressed: Record<Tap, boolean> = { jab: false, toss: false, super: false };

  constructor(
    private readonly audio: GameAudio,
    private readonly callbacks: FightCallbacks,
  ) {
    const canvas = h('canvas', { class: 'fstage__canvas', 'aria-label': 'Shift-change fight' });
    this.stage = new FightStage(canvas);

    this.coolFill = h('i', { class: 'fbar__fill fbar__fill--cool' });
    this.rageFill = h('i', { class: 'fbar__fill fbar__fill--rage' });
    this.superFill = h('i', { class: 'fsuper__fill' });
    this.superWrap = h(
      'div',
      { class: 'fsuper' },
      h('span', { class: 'fsuper__label' }, 'ALL THE WAY'),
      h('div', { class: 'fsuper__bar' }, this.superFill),
    );
    this.oppName = h('span', { class: 'fhud__name fhud__name--opp' });
    this.timer = h('span', { class: 'fhud__timer' }, '40');
    this.pips = h('div', { class: 'fhud__pips' });
    this.hud = h(
      'div',
      { class: 'fhud' },
      h(
        'div',
        { class: 'fhud__side' },
        h('span', { class: 'fhud__name' }, 'THE COOK'),
        h('div', { class: 'fbar' }, this.coolFill, h('span', { class: 'fbar__label' }, 'COOL')),
        this.superWrap,
      ),
      h('div', { class: 'fhud__mid' }, this.timer, this.pips),
      h(
        'div',
        { class: 'fhud__side fhud__side--opp' },
        this.oppName,
        h(
          'div',
          { class: 'fbar fbar--opp' },
          this.rageFill,
          h('span', { class: 'fbar__label' }, 'RAGE'),
        ),
      ),
    );
    this.combo = h('div', { class: 'fcombo' });
    this.bannerText = h('div', { class: 'fbanner__text' });
    this.bannerSub = h('div', { class: 'fbanner__sub' });
    this.banner = h('div', { class: 'fbanner', role: 'status' }, this.bannerText, this.bannerSub);
    this.callouts = h('div', { class: 'fcallouts', 'aria-hidden': 'true' });
    this.bubble = h('div', { class: 'fbubble' });
    const face = h('canvas', { class: 'fdottie__face' });
    paintSpriteInto(face, dottieSprite('talk'), 2);
    this.dottieLine = h('div', { class: 'fdottie__line' });
    this.dottie = h(
      'div',
      { class: 'fdottie' },
      face,
      h('div', { class: 'fdottie__bubble' }, h('b', null, 'DOTTIE'), this.dottieLine),
    );

    this.stageEl = h(
      'div',
      { class: 'fstage' },
      canvas,
      h(
        'div',
        { class: 'fstage__overlay' },
        this.hud,
        this.combo,
        this.callouts,
        this.bubble,
        this.dottie,
        this.banner,
      ),
    );

    const holdButton = (key: Held, label: string, cls: string): HTMLButtonElement => {
      const b = h('button', { class: `fbtn ${cls}`, type: 'button', 'aria-label': key }, label);
      const release = (): void => {
        this.held[key] = false;
        b.classList.remove('is-down');
      };
      b.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        b.setPointerCapture?.(e.pointerId);
        this.held[key] = true;
        b.classList.add('is-down');
      });
      b.addEventListener('pointerup', release);
      b.addEventListener('pointercancel', release);
      b.addEventListener('lostpointercapture', release);
      b.addEventListener('contextmenu', (e) => e.preventDefault());
      return b;
    };
    const tapButton = (key: Tap, label: string, cls: string): HTMLButtonElement => {
      const b = h('button', { class: `fbtn ${cls}`, type: 'button' }, label);
      onPress(b, () => {
        this.pressed[key] = true;
        b.classList.add('is-down');
        setTimeout(() => b.classList.remove('is-down'), 90);
      });
      b.addEventListener('contextmenu', (e) => e.preventDefault());
      return b;
    };
    this.superButton = tapButton('super', 'ALL THE WAY', 'fbtn--super');
    const pad = h(
      'div',
      { class: 'fpad' },
      h(
        'div',
        { class: 'fpad__move' },
        holdButton('left', '◀', 'fbtn--dir'),
        holdButton('right', '▶', 'fbtn--dir'),
      ),
      h(
        'div',
        { class: 'fpad__act' },
        holdButton('block', 'BLOCK', 'fbtn--block'),
        tapButton('jab', 'JAB', 'fbtn--jab'),
        tapButton('toss', 'HASH BROWN', 'fbtn--toss'),
        this.superButton,
      ),
    );
    this.keysHint = h(
      'p',
      { class: 'fkeys' },
      h('kbd', null, '← →'),
      ' move · ',
      h('kbd', null, 'J'),
      ' jab (×3 = spatula smack) · ',
      h('kbd', null, 'K'),
      ' throw hash brown · hold ',
      h('kbd', null, 'L'),
      ' block (just before a hit = PARRY) · ',
      h('kbd', null, 'SPACE'),
      ' ALL THE WAY',
    );

    const pauseButton = h(
      'button',
      { class: 'fpause-btn', type: 'button', 'aria-label': 'Pause' },
      '❚❚',
    );
    pauseButton.addEventListener('click', () => this.pause());
    this.tip = h('p', { class: 'ftip', 'aria-live': 'polite' });
    this.label = h('span', { class: 'ftop__label' });
    this.card = h('div', { class: 'fcard-wrap' });
    this.pauseCard = h('div', { class: 'fcard-wrap', hidden: true });

    this.el = h(
      'section',
      { class: 'fight-screen', 'aria-label': 'Shift change: calm the crowd' },
      h(
        'header',
        { class: 'ftop' },
        h('span', { class: 'ftop__title' }, 'SHIFT CHANGE'),
        this.label,
        pauseButton,
      ),
      h('div', { class: 'fmain' }, this.stageEl, this.tip, pad, this.keysHint),
      this.card,
      this.pauseCard,
    );
    this.el.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'touch') this.el.classList.add('is-touch');
    });
  }

  get active(): boolean {
    return this.mode !== 'idle';
  }

  /* ───────────────────────── flow ───────────────────────── */

  start(options: FightStartOptions): void {
    this.options = options;
    this.interlude = new Interlude({ level: options.level, seed: options.seed });
    this.stage.reducedMotion = options.reducedMotion;
    this.stage.seatCrowd(options.seed);
    toggleClass(this.el, 'show-keys', options.showKeys);
    setText(this.label, options.label);
    this.clearBout();
    this.paused = false;
    this.pauseCard.hidden = true;
    this.audio.music.play('showdown');
    this.showIntro();
  }

  private showIntro(): void {
    const il = this.interlude;
    const opts = this.options;
    if (!il || !opts) return;
    this.mode = 'intro';
    toggleClass(this.hud, 'is-on', false);
    const lineup = h(
      'div',
      { class: 'flineup' },
      ...il.opponents.map((o, i) =>
        h(
          'div',
          { class: `flineup__slot flineup__slot--t${o.tier}` },
          portrait(o.side, 'flineup__art'),
          h('b', null, o.name),
          h('small', null, i === il.opponents.length - 1 ? 'FINAL ROUND' : `ROUND ${i + 1}`),
        ),
      ),
    );
    const start = h('button', { class: 'cta', type: 'button' }, 'LET’S DANCE');
    const skip = h(
      'button',
      { class: 'btn-ghost', type: 'button' },
      opts.practice ? 'back to menu' : 'skip (no bonus)',
    );
    onPress(start, () => {
      this.card.replaceChildren();
      this.card.hidden = true;
      this.startBout();
    });
    onPress(skip, () => this.finish(null, opts.practice ? 'menu' : 'clockIn'));
    const bonus = Math.round(CALM_PATIENCE_PER_CUSTOMER * 100);
    this.card.hidden = false;
    this.card.replaceChildren(
      h(
        'div',
        { class: 'fcard' },
        h('h2', { class: 'fcard__title' }, opts.practice ? 'CALM THE CROWD' : 'SHIFT CHANGE'),
        h(
          'p',
          { class: 'fcard__lede' },
          opts.practice
            ? 'Practice round. Three rowdy customers. Calm them down.'
            : 'The 3AM crowd won’t leave, and the day shift is late. Calm them down before you clock back in.',
        ),
        lineup,
        h(
          'p',
          { class: 'fcard__bonus' },
          `Every customer you calm: +${bonus}% patience next shift, and they come back as big tippers.`,
        ),
        h(
          'ul',
          { class: 'fcard__how' },
          h('li', null, h('b', null, 'JAB'), ' drains their RAGE. Three in a row = spatula smack.'),
          h('li', null, h('b', null, 'HASH BROWN'), ' — throw one from range.'),
          h(
            'li',
            null,
            h('b', null, 'BLOCK'),
            ' when they wind up (they flash red). Block right before the hit to PARRY.',
          ),
          h('li', null, 'Fill the meter, then go ', h('b', null, 'ALL THE WAY'), '.'),
        ),
        h('div', { class: 'fcard__actions' }, start, skip),
      ),
    );
    start.focus();
  }

  private startBout(): void {
    const il = this.interlude;
    if (!il || il.done) return;
    this.clearBout();
    const bout = il.createBout();
    this.bout = bout;
    this.mode = 'bout';
    this.acc = 0;
    this.pressed = { jab: false, toss: false, super: false };
    this.unsub = bout.events.onAny((e) => this.onEvent(e, bout));
    setText(this.oppName, bout.opponentDef.name);
    this.pips.replaceChildren(
      ...il.opponents.map((_, i) => {
        const rec = il.records[i];
        const cls = rec
          ? rec.outcome === 'calmed'
            ? 'is-won'
            : 'is-lost'
          : i === il.index
            ? 'is-now'
            : '';
        return h('i', { class: `fpip ${cls}` });
      }),
    );
    toggleClass(this.hud, 'is-on', true);
  }

  private clearBout(): void {
    this.unsub?.();
    this.unsub = null;
    this.bout = null;
    this.callouts.replaceChildren();
    toggleClass(this.banner, 'is-on', false);
    toggleClass(this.bubble, 'is-on', false);
    toggleClass(this.dottie, 'is-on', false);
    setText(this.combo, '');
  }

  private boutDone(): void {
    const il = this.interlude;
    const bout = this.bout;
    if (!il || !bout) return;
    il.finish(bout);
    if (!il.done) {
      this.startBout();
      return;
    }
    this.showSummary(il.result());
  }

  private showSummary(result: InterludeResult): void {
    const opts = this.options;
    if (!opts) return;
    this.mode = 'summary';
    this.clearBout();
    toggleClass(this.hud, 'is-on', false);
    this.audio.music.play('raisins');
    const unlocked = opts.unlock(result);
    const pct = Math.round(result.calmed * CALM_PATIENCE_PER_CUSTOMER * 100);
    const clockIn = h('button', { class: 'cta', type: 'button' }, 'CLOCK IN ▸');
    onPress(clockIn, () => this.finish(result, 'clockIn'));
    const menu = opts.practice ? h('button', { class: 'btn-ghost', type: 'button' }, 'menu') : null;
    if (menu) onPress(menu, () => this.finish(result, 'menu'));
    const headline =
      result.calmed === result.total
        ? 'EVERYBODY’S CALM.'
        : result.calmed === 0
          ? 'NOBODY CALMED DOWN.'
          : 'MOSTLY CALM.';
    this.card.hidden = false;
    this.card.replaceChildren(
      h(
        'div',
        { class: 'fcard' },
        h('h2', { class: 'fcard__title' }, headline),
        h('p', { class: 'fcard__score' }, `CALMED ${result.calmed} / ${result.total}`),
        h(
          'div',
          { class: 'flineup' },
          ...result.bouts.map((b) =>
            h(
              'div',
              { class: `flineup__slot ${b.outcome === 'calmed' ? 'is-calm' : 'is-mad'}` },
              portrait(b.opponent.side, 'flineup__art'),
              h('b', null, b.opponent.name),
              h(
                'small',
                null,
                b.outcome === 'calmed'
                  ? b.flawless
                    ? '💛 CALMED · FLAWLESS'
                    : '💛 CALMED'
                  : '😤 STILL MAD',
              ),
            ),
          ),
        ),
        h(
          'p',
          { class: 'fcard__stats' },
          [
            `${result.stats.hits} hits`,
            result.stats.maxCombo >= 2 ? `best combo ${result.stats.maxCombo}` : '',
            result.stats.parries
              ? `${result.stats.parries} ${result.stats.parries === 1 ? 'parry' : 'parries'}`
              : '',
            result.stats.supers ? `ALL THE WAY ×${result.stats.supers}` : '',
            result.stats.chairCatches ? `${result.stats.chairCatches} chair caught` : '',
          ]
            .filter(Boolean)
            .join(' · '),
        ),
        h(
          'p',
          { class: 'fcard__bonus' },
          result.calmed > 0
            ? `Next shift: customers are ${pct}% more patient, and ${result.calmed} of tonight’s troublemakers will be back — tipping.`
            : 'No bonus this time. The grill doesn’t care. Clock in.',
        ),
        ...kids(
          unlocked.length
            ? h(
                'div',
                { class: 'fcard__unlocks' },
                ...unlocked.map((a) =>
                  h(
                    'div',
                    { class: 'unlock' },
                    h('span', null, a.emoji),
                    h('b', null, a.title),
                    h('span', null, a.description),
                  ),
                ),
              )
            : null,
        ),
        h('div', { class: 'fcard__actions' }, clockIn, ...kids(menu)),
      ),
    );
    clockIn.focus();
  }

  private finish(result: InterludeResult | null, next: 'clockIn' | 'menu'): void {
    this.clearBout();
    this.mode = 'idle';
    this.card.hidden = true;
    this.card.replaceChildren();
    this.callbacks.onComplete(result, next);
  }

  /* ───────────────────────── pause ───────────────────────── */

  pause(): void {
    if (this.mode !== 'bout' || this.paused) return;
    this.paused = true;
    this.audio.pause();
    const resume = h('button', { class: 'cta', type: 'button' }, 'BACK IN THERE');
    const skip = h('button', { class: 'btn-secondary', type: 'button' }, 'SKIP THE FIGHT');
    const quit = h('button', { class: 'btn-ghost', type: 'button' }, 'quit to menu');
    resume.addEventListener('click', () => this.resume());
    skip.addEventListener('click', () => {
      this.resume();
      const il = this.interlude;
      this.finish(
        il && il.records.length ? il.result() : null,
        this.options?.practice ? 'menu' : 'clockIn',
      );
    });
    quit.addEventListener('click', () => {
      this.resume();
      this.finish(null, 'menu');
    });
    this.pauseCard.hidden = false;
    this.pauseCard.replaceChildren(
      h(
        'div',
        { class: 'fcard fcard--small' },
        h('h2', { class: 'fcard__title' }, 'TIMEOUT'),
        h('p', { class: 'fcard__lede' }, 'Everybody freeze. Dottie’s refilling coffee.'),
        h('div', { class: 'fcard__actions fcard__actions--stack' }, resume, skip, quit),
      ),
    );
    resume.focus();
  }

  resume(): void {
    if (!this.paused) return;
    this.paused = false;
    this.pauseCard.hidden = true;
    this.pauseCard.replaceChildren();
    this.audio.resume();
  }

  /* ───────────────────────── input ───────────────────────── */

  /** Returns true if the key was handled. */
  handleKey(e: KeyboardEvent, down: boolean): boolean {
    if (this.mode !== 'bout') return false;
    const action = KEYMAP[e.key.toLowerCase()];
    if (!action) return false;
    if (action === 'pause') {
      if (down && !e.repeat) {
        if (this.paused) this.resume();
        else this.pause();
      }
      return true;
    }
    if (this.paused) return true;
    if (action === 'left' || action === 'right' || action === 'block') {
      this.held[action] = down;
    } else if (down && !e.repeat) {
      this.pressed[action] = true;
    }
    return true;
  }

  releaseAll(): void {
    this.held.left = false;
    this.held.right = false;
    this.held.block = false;
  }

  /* ───────────────────────── events → presentation ───────────────────────── */

  private toCss(x: number, y: number): { left: number; top: number } {
    const s = this.stageEl.clientWidth / STAGE.width;
    return { left: x * s, top: y * s };
  }

  private onEvent(e: FightEvent, bout: Bout): void {
    this.stage.handle(e, bout);
    switch (e.type) {
      case 'banner':
        setText(this.bannerText, e.text);
        setText(this.bannerSub, e.sub ?? '');
        this.banner.dataset.style = e.style;
        this.banner.classList.remove('is-on');
        void this.banner.offsetWidth;
        this.banner.classList.add('is-on');
        this.bannerUntil = this.now + e.duration;
        if (e.style === 'fight')
          this.audio.voice.say(`Round ${bout.options.round}. Fight!`, {
            rate: 0.9,
            pitch: 0.5,
            interrupt: true,
            volume: 1,
          });
        if (e.style === 'good')
          this.audio.voice.say('Calmed down!', {
            rate: 0.9,
            pitch: 0.6,
            interrupt: true,
            volume: 1,
          });
        if (e.style === 'lose')
          this.audio.voice.say('You lost your cool.', {
            rate: 0.85,
            pitch: 0.5,
            interrupt: true,
            volume: 1,
          });
        if (e.style === 'super')
          this.audio.voice.say('All the way!', { rate: 1, pitch: 0.7, interrupt: true, volume: 1 });
        break;
      case 'callout': {
        const pos = this.toCss(e.x + (Math.random() - 0.5) * 16, STAGE.floorY - SIDE_H - 6);
        const el = h(
          'div',
          {
            class: `fcallout${e.big ? ' is-big' : ''}${e.side === 'player' ? ' is-player' : ''}`,
            style: `left:${pos.left}px;top:${pos.top}px`,
          },
          e.text,
        );
        el.addEventListener('animationend', () => el.remove());
        this.callouts.append(el);
        while (this.callouts.childElementCount > 8) this.callouts.firstElementChild?.remove();
        break;
      }
      case 'say':
        if (e.who === 'dottie') {
          setText(this.dottieLine, e.text);
          toggleClass(this.dottie, 'is-on', true);
          this.dottieUntil = this.now + e.duration + 0.4;
        } else if (e.who === 'opponent') {
          setText(this.bubble, e.text);
          toggleClass(this.bubble, 'is-on', true);
          this.bubbleUntil = this.now + e.duration + 0.3;
        }
        break;
      case 'sfx':
        this.audio.sfx.playFight(e.name);
        break;
      case 'phase':
        if (e.phase === 'done') this.boutDone();
        break;
      default:
        break;
    }
  }

  /* ───────────────────────── loop ───────────────────────── */

  frame(dt: number): void {
    if (this.mode === 'idle') return;
    this.now += dt;
    const bout = this.bout;
    if (bout && this.mode === 'bout' && !this.paused) {
      this.acc = Math.min(this.acc + dt, 0.1);
      const step = 1 / 60;
      while (this.acc >= step && this.bout === bout) {
        const input: PlayerInput = { ...this.held, ...this.pressed };
        this.pressed = { jab: false, toss: false, super: false };
        bout.update(step, input);
        this.acc -= step;
      }
    }
    const current = this.bout;
    this.stage.render(current, this.paused ? 0 : dt);
    if (current) this.updateHud(current);
    if (this.mode === 'bout' && this.now >= this.tipAt) {
      this.tipAt = this.now + 6;
      setText(this.tip, `💡 ${TIPS[this.tipIndex++ % TIPS.length] ?? ''}`);
    }
    if (this.now > this.bannerUntil) toggleClass(this.banner, 'is-on', false);
    if (this.now > this.dottieUntil) toggleClass(this.dottie, 'is-on', false);
    if (this.now > this.bubbleUntil) toggleClass(this.bubble, 'is-on', false);
  }

  private updateHud(b: Bout): void {
    this.coolFill.style.transform = `scaleX(${b.player.meter / b.player.maxMeter})`;
    this.rageFill.style.transform = `scaleX(${b.opponent.meter / b.opponent.maxMeter})`;
    this.superFill.style.transform = `scaleX(${Math.min(1, b.superMeter / BOUT.superCost)})`;
    toggleClass(this.superWrap, 'is-ready', b.superReady);
    toggleClass(this.superButton, 'is-ready', b.superReady);
    setText(this.timer, String(Math.ceil(b.timeLeft)));
    toggleClass(this.timer, 'is-low', b.timeLeft < 10);
    setText(this.combo, b.combo >= 2 ? `${b.combo} HIT COMBO` : '');
    if (this.bubble.classList.contains('is-on')) {
      const pos = this.toCss(b.opponent.x, STAGE.floorY - SIDE_H - 4);
      this.bubble.style.left = `${pos.left}px`;
      this.bubble.style.top = `${pos.top}px`;
    }
  }
}
